import { FC, useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { InputField } from '@/components/Form';
import PhoneInputField from '@/components/Form/PhoneInputField';
import { Button, Divider, Space, Spin, Typography } from 'antd';
import {
  useGetClientTokenAsUserMutation,
  useRegisterClientByAdminMutation,
  useVerifyExistingUserMutation,
} from '@/store/apis/odinAppApi';
import { displayError } from '@/utils/display-message-utils';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/hooks/reduxHooks';
import {
  clearClientToken,
  setClientToken,
} from '@/store/slices/auth/authSlice';
import { HubspotContact } from '@/shared/types';
const createClientSchema = yup.object().shape({
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  email: yup
    .string()
    .email('Please enter a valid email')
    .required('Email is required'),
  phone: yup.string(),
});

export type CreateClientFormType = yup.InferType<typeof createClientSchema>;

interface CreateClientFormProps {
  contact: HubspotContact | null;
  redirectUri: string | null;
}
const CreateClientForm: FC<CreateClientFormProps> = ({
  contact,
  redirectUri,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isExistingUser, setIsExistingUser] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useForm({
    resolver: yupResolver(createClientSchema),
    defaultValues: {},
  });

  const [getClientToken, { isLoading: getClientTokenLoading }] =
    useGetClientTokenAsUserMutation();

  const [registerClientByAdmin, { isLoading: isRegisterClientLoading }] =
    useRegisterClientByAdminMutation();

  const [verifyExistingUser, { isLoading: verifyExistingUserLoading }] =
    useVerifyExistingUserMutation();

  const handleLoginAsUser = async (email: string) => {
    dispatch(clearClientToken());
    getClientToken({ email }) // Make sure your mutation accepts an object with `email`
      .unwrap()
      .then((response) => {
        dispatch(setClientToken({ clientToken: response.data.access_token }));
        if (redirectUri) {
          navigate(redirectUri);
        }
      })
      .catch((err) => {
        console.error('Token fetch error:', err);
        displayError(err);
      });
  };

  const handleVerifyExistingUser = (
    email: string,
    isContactFromProps: boolean = false,
  ) => {
    setIsExistingUser(false);
    verifyExistingUser({ email })
      .unwrap()
      .then((response) => {
        setIsExistingUser(response.data.exists);
        if (response.data.exists && isContactFromProps) {
          handleLoginAsUser(email);
        }
      })
      .catch((err) => {
        setIsExistingUser(false);
        displayError(err);
      });
  };
  const handleEmailFieldBlur = () => {
    const email = getValues('email');
    if (email) {
      handleVerifyExistingUser(email);
    }
  };
  const onSubmit: SubmitHandler<CreateClientFormType> = (data) => {
    dispatch(clearClientToken());
    registerClientByAdmin(data)
      .unwrap()
      .then((res) => {
        dispatch(setClientToken({ clientToken: res.data.access_token }));
        if (redirectUri) {
          navigate(redirectUri);
        }
      })
      .catch((err) => {
        console.log('registerClient', err);

        displayError(err);
      });
  };

  useEffect(() => {
    if (contact) {
      setValue('email', contact?.properties?.email ?? '');
      setValue('firstName', contact?.properties?.firstname ?? '');
      setValue('lastName', contact?.properties?.lastname ?? '');
      setValue('phone', contact?.properties?.phone ?? '');

      if (contact?.properties?.email) {
        handleVerifyExistingUser(contact?.properties?.email, true);
      }
    }
  }, [contact]);

  const handleStartAssessment = () => {
    const email = getValues('email');
    handleLoginAsUser(email);
  };
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputField
          size="large"
          name="email"
          label="Email"
          placeholder="Enter your email"
          control={control}
          error={errors.email?.message ?? ''}
          required={true}
          onBlur={handleEmailFieldBlur}
        />
        <InputField
          size="large"
          name="firstName"
          label="First Name"
          placeholder="Enter your first name"
          control={control}
          error={errors.firstName?.message ?? ''}
          required={true}
        />
        <InputField
          size="large"
          name="lastName"
          label="Last Name"
          placeholder="Enter your last name"
          control={control}
          error={errors.lastName?.message ?? ''}
          required={true}
        />

        <PhoneInputField
          label="Phone Number"
          name="phone"
          control={control}
          error={errors.phone?.message ?? ''}
        />
        <Button
          htmlType="submit"
          type="primary"
          disabled={isRegisterClientLoading}
          loading={isRegisterClientLoading}
        >
          Register Contact
        </Button>
      </form>

      <Divider />

      {verifyExistingUserLoading && (
        <Space style={{ marginTop: '2rem' }}>
          <Spin tip="Verifying contact">
            <div style={{ width: '150px' }}></div>
          </Spin>
        </Space>
      )}

      {isExistingUser && (
        <div style={{ marginTop: '1rem' }}>
          <Typography.Title level={5}>
            Contact alredy registered.
          </Typography.Title>
          <Button type="primary" onClick={handleStartAssessment}>
            Start Assessment
          </Button>
        </div>
      )}

      {getClientTokenLoading && (
        <Space style={{ marginTop: '2rem' }}>
          <Spin tip="Starting Assessment">
            <div style={{ width: '150px' }}></div>
          </Spin>
        </Space>
      )}
    </>
  );
};

export default CreateClientForm;
